import { useState, type ReactNode } from "react";
import { Briefcase, ExternalLink, Loader2, MapPin, Star, UserRound, Users, Zap } from "lucide-react";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AddContactModal } from "@/components/add/AddContactModal";
import { ActivityTimeline } from "@/components/activity/ActivityTimeline";
import { EntityRemindersButton } from "@/components/reminders/EntityRemindersButton";
import { StarProspectPopover } from "@/components/financials/StarProspectPopover";
import { OwnerPicker } from "@/components/OwnerPicker";
import { SourceCard } from "@/components/origination/SourceCard";
import { ParkDialog } from "@/components/prospects/ParkDialog";
import { ConvertToClientDialog } from "@/components/prospects/ConvertToClientDialog";
import { InlineEdit } from "@/components/InlineEdit";
import { useAuth } from "@/lib/auth";
import { useCurrentUserTimezone } from "@/hooks/useCurrentUserTimezone";
import { useCompanyField } from "@/hooks/useCompanyField";
import { useCompanySummary } from "@/hooks/useRecordSummary";
import { useOrigination } from "@/hooks/useOrigination";
import { useProspectActions } from "@/hooks/useProspectActions";
import { RecordFrame, RecordPanel } from "@/components/record/RecordFrame";
import { RecordHeader, RecordChip, type SublineItem } from "@/components/record/RecordHeader";
import { FactsStrip } from "@/components/record/FactsStrip";
import { ActionBar } from "@/components/record/ActionBar";
import { BriefPanel } from "@/components/record/BriefPanel";
import { EditableTextPanel } from "@/components/record/EditableTextPanel";
import { CompanyRail } from "@/components/record/CompanyRail";
import { CompanyReach } from "@/components/record/CompanyReach";
import { companyLinkItems } from "@/components/record/LinkField";
import { CompanyNameEdit } from "@/components/record/CompanyNameEdit";
import { CompanyMoreMenu } from "@/components/record/CompanyMoreMenu";
import { ArchiveCompanyDialog } from "@/components/record/ArchiveCompanyDialog";
import { useRecordCompose } from "@/components/record/useRecordCompose";
import { prospectFacts } from "@/components/record/facts/companyFacts";
import { downloadText, fullDay, localYmd, primaryContact, sourceLabel } from "@/components/record/recordFormat";
import type { CompanyData } from "./companyTypes";
import { StageTrackPanel } from "./prospect/StageTrackPanel";
import { MvaPanel } from "./prospect/MvaPanel";
import { ProspectSnapshot } from "./prospect/ProspectSnapshot";
import { ProspectEnrichReview } from "./prospect/ProspectEnrichReview";
import { useProspectEnrichment } from "./prospect/useProspectEnrichment";

// interest_level stores "Cool"; the team calls it Cold.
const PRIORITY = {
  Hot: { label: "Hot", dot: "bg-red-500" },
  Warm: { label: "Warm", dot: "bg-amber-500" },
  Cool: { label: "Cold", dot: "bg-blue-400" },
} as const;
type Priority = keyof typeof PRIORITY;

export default function ProspectRecord({ company }: { company: CompanyData }) {
  const userTz = useCurrentUserTimezone();
  const { canViewFinancials } = useAuth();
  const { saveField, saveFieldAsync } = useCompanyField(company.id);
  const summary = useCompanySummary(company.id);
  const origination = useOrigination(company.id);
  const enrich = useProspectEnrichment(company);
  const { park, resume } = useProspectActions(company.id);
  const { compose, setCompose, openLog, nextStepOpen, setNextStepOpen } = useRecordCompose();
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [parkOpen, setParkOpen] = useState(false);

  const people = company.contacts ?? [];
  const primary = primaryContact(people);
  const today = localYmd(new Date());
  const e = company.enrichmentData;
  const parked = company.status === "On Hold";
  const priority = PRIORITY[(company.interestLevel as Priority) ?? "Cool"] ?? PRIORITY.Cool;
  const onResume = () => resume.mutate(undefined, {
    onSuccess: () => toast.success("Back in the active list"),
    onError: (err: any) => toast.error(err.message),
  });

  const subline: SublineItem[] = [
    ...(company.industry ? [{ icon: <Briefcase />, content: company.industry }] : []),
    { icon: <MapPin />, content: <InlineEdit label="Location" value={company.headquarters} placeholder="City, State" addLabel="Add location" onSave={(v) => saveFieldAsync("headquarters", v.trim() || null)} /> },
    ...(company.employeeCount ? [{ icon: <Users />, content: /employee/i.test(company.employeeCount) ? company.employeeCount : `${company.employeeCount} employees` }] : []),
    ...(company.revenueRange ? [{ content: `Revenue ${company.revenueRange}` }] : []),
    ...companyLinkItems({ website: company.website, linkedinUrl: company.linkedinUrl, onSave: (field, v) => saveFieldAsync(field, v) }),
    ...(e?.apolloId ? [{ icon: <ExternalLink />, content: <a href={`https://app.apollo.io/#/organizations/${e.apolloId}`} target="_blank" rel="noopener noreferrer" className="hover:underline">Apollo</a> }] : []),
    { icon: <UserRound />, content: <span className="inline-flex items-center gap-1.5">Owner <OwnerPicker value={company.ownerUserId} onChange={(v) => saveField("ownerUserId", v)} /></span> },
  ];

  const header = (
    <RecordHeader
      crumb={{ label: "Prospects", href: "/prospects" }}
      current={company.name}
      name={<CompanyNameEdit value={company.name} onSave={(t) => saveFieldAsync("name", t)} />}
      chips={<>
        <RecordChip tone="category">Prospect</RecordChip>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" aria-label={`Priority: ${priority.label}`} className="rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
              <RecordChip><span className={`h-1.5 w-1.5 rounded-full ${priority.dot}`} />{priority.label}</RecordChip>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {(Object.keys(PRIORITY) as Priority[]).map((k) => (
              <DropdownMenuItem key={k} onSelect={() => saveField("interestLevel", k)}>{PRIORITY[k].label}</DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {parked && <RecordChip tone="warn">Nurturing</RecordChip>}
        {company.engagementType && <RecordChip>{company.engagementType}</RecordChip>}
        {people.some((p) => p.engaged) && <RecordChip tone="good">Spoken with</RecordChip>}
      </>}
      subline={subline}
      reach={<CompanyReach people={people} mainPhone={e?.phone} />}
      actions={
        <ActionBar onLog={openLog} email={primary?.email} onNextStep={() => setNextStepOpen(true)}>
          <EntityRemindersButton entityType="company" entityId={company.id} entityName={company.name} userTimezone={userTz} />
          {canViewFinancials && (
            <StarProspectPopover
              prospect={{ id: company.id, name: company.name, estimatedDealValue: company.estimatedDealValue, expectedFeePercentage: company.expectedFeePercentage }}
              starred={!!company.financialsStarredAt}
            >
              <Button size="sm" variant="outline" className="gap-1.5">
                <Star className={`h-3.5 w-3.5 ${company.financialsStarredAt ? "fill-current" : ""}`} />
                {company.financialsStarredAt ? "Starred" : "Star"}
              </Button>
            </StarProspectPopover>
          )}
          <Button size="sm" variant="outline" className="gap-1.5" onClick={enrich.run} disabled={enrich.phase === "apollo"}>
            {enrich.phase === "apollo" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
            {enrich.phase === "apollo" ? "Enriching…" : "Enrich"}
          </Button>
          <CompanyMoreMenu company={company} onArchive={() => setArchiveOpen(true)}>
            {parked ? (
              <DropdownMenuItem onSelect={onResume}>Resume from nurture</DropdownMenuItem>
            ) : (
              <DropdownMenuItem onSelect={() => setParkOpen(true)}>Park in nurture</DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={() => setConvertOpen(true)}>Convert to client</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => downloadText(`${company.name}.txt`, `Company: ${company.name}\nIndustry: ${company.industry || ""}\nRevenue: ${company.revenueRange || ""}`)}>Export</DropdownMenuItem>
          </CompanyMoreMenu>
        </ActionBar>
      }
    />
  );

  const work = (
    <>
      <BriefPanel
        summary={summary.data}
        loading={summary.isLoading}
        error={summary.isError}
        onRetry={() => summary.refetch()}
        openLabel={`Open on ${company.name}`}
        target={{ companyId: company.id }}
        nextStepOpen={nextStepOpen}
        onNextStepOpenChange={setNextStepOpen}
      />
      <StageTrackPanel
        stage={company.stage}
        parked={parked}
        revisitOn={company.holdRevisitDate ?? null}
        holdReason={company.holdReason ?? null}
        moves={summary.data?.stageMoves}
        onStage={(s) => saveField("stage", s)}
        onConvert={() => setConvertOpen(true)}
        onPark={() => setParkOpen(true)}
        onResume={onResume}
        resuming={resume.isPending}
      />
      <MvaPanel company={{ id: company.id, name: company.name, industry: company.industry }} />
      <EditableTextPanel
        title="About"
        value={company.description}
        onSave={(v) => saveFieldAsync("description", v.trim() || null)}
        emptyText="No description yet. Enrich to pull company info, or add it here."
      />
      <ProspectSnapshot company={company} saveCompanyFieldAsync={saveFieldAsync} />
      <RecordPanel id="record-activity">
        <ActivityTimeline
          scope={{ kind: "company", companyId: company.id, companyName: company.name, contacts: people }}
          compose={compose}
          onComposeChange={setCompose}
        />
      </RecordPanel>
    </>
  );

  const details: [string, ReactNode][] = [
    ...(e?.foundedYear ? [["Founded", String(e.foundedYear)] as [string, ReactNode]] : []),
    ...(e?.totalFunding ? [["Total funding", e.totalFunding] as [string, ReactNode]] : []),
    ...(e?.enrichedAt ? [["Enriched", fullDay(e.enrichedAt)] as [string, ReactNode]] : []),
    ["Added", fullDay(company.createdAt)],
    ["Entered via", sourceLabel(company.creationSource)],
    ["Added by", company.creator?.name ?? "Unknown"],
  ];

  return (
    <AppLayout title={company.name}>
      <RecordFrame
        header={header}
        facts={summary.data
          ? <FactsStrip facts={prospectFacts(summary.data, today, {
              stage: company.stage, status: company.status, holdRevisitDate: company.holdRevisitDate ?? null,
              lastActivityAt: company.lastActivityAt ?? null, targetAmount: company.targetAmount,
              estimatedDealValue: company.estimatedDealValue ?? null, aiDealValue: e?.estimatedDealValue ?? null,
            }, origination.data)} />
          : summary.isLoading ? <div className="h-[58px] animate-pulse rounded-lg border bg-card" /> : null}
        work={work}
        rail={
          <CompanyRail
            company={company}
            summary={summary}
            details={details}
            detailsExtra={<div className="mt-2.5 border-t pt-2.5"><SourceCard companyId={company.id} compact /></div>}
            notesTitle="Why they should sell"
            onSaveNotes={(v) => saveFieldAsync("notes", v.trim() || null)}
            onAddPerson={() => setAddPersonOpen(true)}
          />
        }
      />
      <ProspectEnrichReview enrich={enrich} company={company} />
      <AddContactModal open={addPersonOpen} onOpenChange={setAddPersonOpen} preselectedCompanyId={company.id} preselectedCompanyName={company.name} />
      <ArchiveCompanyDialog company={company} open={archiveOpen} onOpenChange={setArchiveOpen} />
      <ConvertToClientDialog open={convertOpen} onOpenChange={setConvertOpen} company={{ id: company.id, name: company.name, engagementType: company.engagementType }} />
      <ParkDialog
        open={parkOpen}
        onOpenChange={setParkOpen}
        pending={park.isPending}
        onConfirm={(v) => park.mutate(v, {
          onSuccess: () => { setParkOpen(false); toast.success("Parked in nurture"); },
          onError: (err: any) => toast.error(err.message),
        })}
      />
    </AppLayout>
  );
}
